import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {ENVIRONMENT} from 'environments-lib';
import { IEnvironment } from 'interfaces-lib';
import { Observable, of, tap } from 'rxjs';
import { ILlm } from './Illm';

@Injectable({
  providedIn: 'root',
})
export class LlmConfigService {
  private _cacheLLM: ILlm[] | undefined;

  private static readonly LEGACY_MODEL_MAPPING: Record<string, string> = {
    'gpt-4o-mini': 'kyloe-ai-4o-mini',
    'claude-3-haiku': 'anthropic.claude-3-haiku-20240307-v1:0',
    'claude-3.5-sonnet': 'anthropic.claude-3-5-sonnet-20240620-v1:0',
    'gpt-4-turbo': 'kyloe-ai-4o',
    'gpt-4o': 'kyloe-ai-4o',
    'sonnet-4': 'anthropic.claude-sonnet-4-20250514-v1:0',
    'gpt-4.1': 'kyloe-ai-41',
  };

  constructor(
    @Inject(ENVIRONMENT) private environment: Partial<IEnvironment>,
    private http: HttpClient,
  ) {}

  getLlmModels(enabledModels: string[] = []): Observable<ILlm[]> {
    if (this._cacheLLM) {
      return of(this._cacheLLM);
    }
    return this.http
      .post<ILlm[]>(`${this.environment.kpWorkflowHost}/ai-assist/llm/models`, { enabledModels })
      .pipe(tap((llm) => (this._cacheLLM = llm)));
  }

  private mapLegacyModelId(oldModelId: string): string {
    return LlmConfigService.LEGACY_MODEL_MAPPING[oldModelId] || oldModelId;
  }

  resolveLlmReplacement(
    modelIdOrName: string,
    llmList: ILlm[],
    requiredProduct?: string,
    visited: Set<string> = new Set(),
  ): ILlm | undefined {
    const mappedModelId = this.mapLegacyModelId(modelIdOrName);

    const model = llmList.find((llm) => llm.modelId === mappedModelId || llm.name === mappedModelId);

    if (!model) {
      return undefined;
    }

    // Prevent infinite loops
    if (visited.has(model.modelId)) {
      console.warn(`Circular replacement detected for model: ${model.modelId}`);
      return model;
    }

    visited.add(model.modelId);

    // If the model has no replacement, this is the final model
    if (!model.replacement) {
      // If product filter is specified, verify the model matches
      if (requiredProduct && !model.products?.includes(requiredProduct)) {
        console.warn(`Model ${model.modelId} does not match required product: ${requiredProduct}`);
        return undefined;
      }
      return model;
    }

    // Recursively resolve the replacement
    const replacementModel = this.resolveLlmReplacement(model.replacement, llmList, requiredProduct, visited);

    // If replacement exists and matches product requirements, return it
    if (replacementModel) {
      return replacementModel;
    }

    if (requiredProduct && !model.products?.includes(requiredProduct)) {
      return undefined;
    }
    return model;
  }
}
